import { Coffee } from 'lucide-react';
import { KOFI } from '../lib/kofi';
import { cn } from '../lib/utils';

interface SupportKoFiButtonProps {
  /**
   * `panel` is the full card that sits directly below the r/FromAfar box on
   * wide viewports. `tab` is the edge tab shown instead, stacked under the
   * subreddit tab, once that box collapses.
   */
  variant?: 'panel' | 'tab';
  className?: string;
}

export function SupportKoFiButton({ variant = 'panel', className }: SupportKoFiButtonProps) {
  if (variant === 'tab') {
    return (
      <a
        data-testid="kofi-tab-button"
        href={KOFI.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={KOFI.label}
        title={KOFI.label}
        className={cn(
          'fixed z-50 transition-all duration-300 border shadow-lg',

          // Mobile (default) - solid opaque background, matching the other rail buttons
          'bg-pink-600 border-pink-500 text-white hover:bg-pink-500 active:bg-pink-700',

          // Desktop/Tablet (768px and up) - transparent with blur
          'sm:bg-gradient-to-br sm:from-pink-900/20 sm:to-rose-900/10',
          'sm:border-pink-500/30 sm:backdrop-blur-sm',
          'sm:text-pink-400 sm:hover:text-pink-300',
          'sm:hover:border-pink-400/50 sm:hover:shadow-xl sm:hover:shadow-pink-500/10',

          // Desktop/Tablet positioning - right edge, one tab height below the
          // subreddit tab (which is h-20 centred on top-1/2).
          'sm:flex sm:top-[calc(50%+5.5rem)] sm:right-0 sm:-translate-y-1/2',
          'sm:border-r-0 sm:rounded-md sm:min-w-[38px] sm:h-20 sm:px-2 sm:py-3',
          'sm:flex-col sm:items-center sm:justify-center sm:gap-1 sm:rounded-l-lg',

          // Mobile positioning - bottom right, stacked above the subreddit
          // (bottom-20) and latest-confirmed (bottom-4) floating buttons.
          'flex bottom-36 right-4 w-11 h-11 rounded-full sm:w-auto sm:h-auto sm:bottom-auto',
          'items-center justify-center shadow-2xl sm:shadow-lg',
          className
        )}
      >
        <Coffee className="h-4 w-4 flex-shrink-0 sm:block hidden" />
        <span className="text-[10px] font-medium whitespace-nowrap sm:block hidden">KO-FI</span>

        {/* Mobile layout - just the icon */}
        <Coffee className="h-5 w-5 sm:hidden block" />
      </a>
    );
  }

  return (
    <div
      data-testid="kofi-support-panel"
      className={cn(
        'relative mt-3 rounded-lg overflow-hidden transition-all duration-300',
        'bg-gradient-to-br from-pink-900/20 to-rose-900/10 border border-pink-500/30 shadow-lg',
        'hover:shadow-xl hover:shadow-pink-500/10 hover:border-pink-400/50',
        'backdrop-blur-sm',
        className
      )}
    >
      {/* Sci-fi corner accents, matching the sibling widgets */}
      <div className="absolute top-0 left-0 w-[40px] h-[1px] bg-gradient-to-r from-pink-400/70 to-transparent" />
      <div className="absolute top-0 left-0 w-[1px] h-[40px] bg-gradient-to-b from-pink-400/70 to-transparent" />
      <div className="absolute top-0 right-0 w-[40px] h-[1px] bg-gradient-to-l from-pink-400/70 to-transparent" />
      <div className="absolute top-0 right-0 w-[1px] h-[40px] bg-gradient-to-b from-pink-400/70 to-transparent" />

      <div className="px-3.5 pt-3.5 pb-2.5 border-b border-pink-500/20">
        <h3 className="flex items-center gap-[7px] text-[15px] font-bold leading-[22px] text-pink-400">
          <Coffee className="h-[17px] w-[17px] flex-shrink-0 text-pink-400" />
          Support the site
        </h3>
      </div>

      <div className="flex flex-col gap-3 p-3.5">
        <p className="text-xs leading-[18px] text-gray-300">
          TheViewShed is free and has no ads. Hosting is not.
        </p>
        <a
          data-testid="kofi-support-button"
          href={KOFI.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={KOFI.label}
          className={cn(
            'flex h-[38px] items-center justify-center gap-2 rounded-md',
            // Ko-fi's own brand coral, so the button is recognisable as one
            'bg-[#ff5e5b] hover:bg-[#ff4542] text-white',
            'text-sm font-semibold leading-5 transition-colors duration-200',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400/70'
          )}
        >
          <Coffee className="h-4 w-4 flex-shrink-0" />
          Support us
        </a>
      </div>
    </div>
  );
}

export default SupportKoFiButton;
